import { useState } from 'react';

import { eventPropsToString } from '@pray/shared/components/events/eventUtils';
import { colors, typography } from '@pray/shared/components/foundations';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@pray/shared/components/ui/Accordion/Accordion';
import FAQ from '@pray/shared/components/ui/FAQ/FAQ';
import Label from '@pray/shared/components/ui/Label/Label';
import { PricingCard } from '@pray/shared/components/ui/PricingCard/PricingCard';
import SegmentedControl from '@pray/shared/components/ui/SegmentedControl';
import Text from '@pray/shared/components/ui/Text/Text';
import { mapPlanToPricing, plans } from '@pray/shared/data/pricing/plans';
import { RecurringPeriod, type Plan } from '@pray/shared/data/pricing/types';
import { getPlanSkus } from '@pray/shared/data/pricing/utils';

import { PRODUCT_LABELS, Products } from 'components/pages/public/types/products';
import { VIEW_EVENT_NAMES } from 'constants.js';

import { SolutionsSection } from '../../products/layout/ProductPageLayout';

type PricingProps = {
  title?: string;
  subtitle?: string;
  showSolutionsSection?: boolean;
  shouldAddEventProps?: boolean;
  onPlanSelect: (plan: Plan, recurringPeriod: RecurringPeriod) => void;
};

const FAQItems = [
  {
    title: 'Can I upgrade my subscription later?',
    content:
      "Absolutely. Pray understands that your organization's needs may change over time. You can upgrade your subscription plan at any point to align with your evolving requirements.",
  },
  {
    title: 'What kind of payments do you accept?',
    content: 'We accept: Credit Card, Apple Pay and Google Pay.',
  },
  {
    title: `Can I cancel ${PRODUCT_LABELS[Products.PRAYSTUDIO]} at any time?`,
    content: `Yes. Simply go to your account settings in ${PRODUCT_LABELS[Products.PRAYSTUDIO]} and cancel your plan.`,
  },
];

export default function Pricing({
  title = 'Choose your plan',
  subtitle = "Choose the plan that fits your ministry's size and vision.",
  showSolutionsSection = true,
  shouldAddEventProps = true,
  onPlanSelect,
}: PricingProps) {
  const options = [
    { value: RecurringPeriod.MONTHLY, label: 'MONTHLY' },
    { value: RecurringPeriod.YEARLY, label: 'YEARLY' },
  ];
  const [planPeriod, setPlanPeriod] = useState<RecurringPeriod>(RecurringPeriod.MONTHLY);
  const eventProps: Record<string, string> = {};

  if (shouldAddEventProps) {
    eventProps['data-viewname'] = VIEW_EVENT_NAMES.PRICING.PRICING;
    eventProps['data-eventprops'] = eventPropsToString({
      sku_group: getPlanSkus(plans),
    });
  }

  return (
    <div className="flex w-full flex-col px-4 md:px-10" {...eventProps}>
      <div className="mb-8 flex flex-col items-center gap-6 md:mb-12 md:gap-8">
        <div className="flex flex-col gap-1 text-center">
          <Text variant={typography.heading_4xl}>{title}</Text>
          <Text variant={typography.body_lg}>{subtitle}</Text>
        </div>
        <div className="flex flex-col gap-2">
          <SegmentedControl
            options={options}
            value={planPeriod}
            onChange={(selectedValue) => setPlanPeriod(selectedValue as RecurringPeriod)}
          />
          <Label status="published">
            <Text variant={typography.button_small}>Save up to 10% with YEARLY BILLING</Text>
          </Label>
        </div>
      </div>
      <div className="mb-6 grid w-full max-w-7xl grid-cols-1 gap-6 self-center md:grid-cols-3">
        {plans.map((plan) => (
          <PricingCard
            key={`${plan.name}-${planPeriod}`}
            plan={mapPlanToPricing(plan, planPeriod)}
            billingPeriod={planPeriod}
            onPlanSelect={() => onPlanSelect(plan, planPeriod)}
          />
        ))}
      </div>

      {showSolutionsSection && <SolutionsSection solutionsSectionTitle="All-in-one Solution" />}

      <FAQ title="Frequently Asked Questions" className="mt-4 text-center">
        <Accordion type="multiple" className="w-full text-left">
          {FAQItems.map((item) => (
            <AccordionItem value={item.title} key={item.title}>
              <AccordionTrigger className="group flex w-full items-center justify-between px-4 py-8 text-left font-bold hover:bg-[var(--theme-bg-secondary)] hover:no-underline">
                <Text
                  variant={typography.heading_md}
                  color={colors.text_secondary}
                  className="group-data-[state=open]:text-text-primary"
                >
                  {item.title}
                </Text>
              </AccordionTrigger>
              <AccordionContent className="px-4">
                <Text variant={typography.body_lg} color={colors.text_secondary} className="text-left">
                  {item.content}
                </Text>
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </FAQ>
    </div>
  );
}
